/**
 * How a Business Login status looks, in one place.
 *
 * The sibling of permit-status.ts and deliberately the same shape, so a status cannot
 * end up green in the CRM list and grey on the business's own settings screen.
 *
 * Three states, and the palette carries the same meaning it does for permits: a SOLID
 * pill is something someone has to act on, a tint is where the record sits.
 */
const STATUS_CLASSES: Record<string, string> = {
    // Applied for and waiting on TLC. Amber because it is somebody's outstanding work,
    // matching "needs info" on a permit rather than the neutral "submitted" - an
    // application nobody looks at is a business that never hears back.
    pending: 'bg-warning-subtle text-warning-emphasis border border-warning-subtle',

    active: 'bg-success-subtle text-success-emphasis border border-success-subtle',

    // Solid, like `revoked` on a permit: suspending is access taken away from an
    // organisation that had it, which is heavier than an application not yet approved
    // and should not read as another shade of grey.
    suspended: 'text-bg-danger border border-danger',
};

const FALLBACK = 'bg-secondary-subtle text-secondary-emphasis border border-secondary-subtle';

/** Bootstrap classes for the pill, minus the badge/pill classes themselves. */
export function businessStatusClass(status?: string | null): string {
    return STATUS_CLASSES[status ?? ''] ?? FALLBACK;
}

/**
 * The pill as markup, for the CRUD list.
 *
 * Takes `unknown` for the same reason the other column formatters do: the CRUD hands a
 * custom column the whole row object when the property is absent, and the API omits
 * null properties. A status should always be present, but "should" is what the other
 * formatters assumed before this one was written.
 */
export function businessStatusPill(value: unknown): string {
    // Accepts the row as well as a bare status: the CRUD list passes the whole row
    // (its column `value` is empty), while the settings screen passes the string.
    const row = (typeof value === 'object' && value !== null ? value : {}) as { status?: string };
    const status = typeof value === 'string' ? value : (row.status ?? '');
    const known = Object.prototype.hasOwnProperty.call(STATUS_CLASSES, status);
    const label = known ? status : 'unknown';

    return `<span class="badge rounded-pill text-capitalize ${businessStatusClass(status)}">${label}</span>`;
}
